import { useCallback, useEffect, useRef, useState } from 'react'

type Thumb = { top: number; height: number; visible: boolean }

export function PageScrollbar() {
  const [thumb, setThumb] = useState<Thumb>({ top: 0, height: 0, visible: false })
  const dragging = useRef(false)
  const startY = useRef(0)
  const startScroll = useRef(0)
  const heightRef = useRef(0)

  const update = useCallback(() => {
    const doc = document.documentElement
    const docH = Math.max(doc.scrollHeight, document.body.scrollHeight)
    const winH = window.innerHeight
    if (docH <= winH + 4) {
      setThumb((t) => (t.visible ? { ...t, visible: false } : t))
      return
    }
    const ratio = winH / docH
    const height = Math.max(40, ratio * winH)
    const maxScroll = docH - winH
    const top = maxScroll > 0 ? (window.scrollY / maxScroll) * (winH - height) : 0
    heightRef.current = height
    setThumb({ top, height, visible: true })
  }, [])

  useEffect(() => {
    update()
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', update)
    const ro = new ResizeObserver(update)
    ro.observe(document.body)
    const mo = new MutationObserver(update)
    mo.observe(document.body, { childList: true, subtree: true })
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', update)
      ro.disconnect()
      mo.disconnect()
    }
  }, [update])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return
      e.preventDefault()
      const docH = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
      )
      const winH = window.innerHeight
      const trackH = winH - heightRef.current
      if (trackH <= 0) return
      const dy = e.clientY - startY.current
      const ratio = (docH - winH) / trackH
      window.scrollTo({ top: startScroll.current + dy * ratio, behavior: 'auto' })
    }
    const onUp = () => {
      if (!dragging.current) return
      dragging.current = false
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  const onTrackClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return
    const winH = window.innerHeight
    const docH = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
    )
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
    const ratio = (e.clientY - rect.top) / rect.height
    window.scrollTo({ top: ratio * (docH - winH), behavior: 'smooth' })
  }

  const onThumbDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragging.current = true
    startY.current = e.clientY
    startScroll.current = window.scrollY
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  if (!thumb.visible) return null
  return (
    <div
      onClick={onTrackClick}
      className="fixed right-1 top-0 z-[100] h-screen w-3 cursor-pointer"
      style={{
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04))',
      }}
    >
      <div
        onMouseDown={onThumbDown}
        className="absolute right-0 w-2.5 cursor-grab rounded-full bg-white/30 backdrop-blur-sm transition hover:bg-white/50 active:cursor-grabbing"
        style={{
          top: thumb.top,
          height: thumb.height,
          boxShadow: '0 0 0 1px rgba(255,255,255,0.08)',
        }}
      />
    </div>
  )
}
